import Phaser from "phaser";
import { EntityKind, resolveColour, type CreatureLook, type EntityState, type NpcLook } from "odyssey-shared";
import { UI_ATLAS_KEY } from "../assets.ts";
import type { WorldUiModule } from "../hook.ts";
import { PIXEL_FONT_KEY, pixelFontSize } from "../pixel-font.ts";
import { MARK, inVisionCap, newPlateLayout, onScreen, plateLayout, plateWidth, type PlateLayout } from "../nameplates/layout.ts";
import { NameplatePool } from "../nameplates/pool.ts";
import { PLATE_LIFT } from "../health-bars/bar.ts";
import type { NameplateChoices } from "../../settings.ts";
import { ATTITUDE_STYLES, attitudeOf, attitudeOfPlate, attitudeTint, creatureBarShown, creaturePlateForm, creatureTextWidth, levelLine, nameOffset, npcFights, rankFrame, type Attitude, type CreaturePlateForm } from "./style.ts";
import type { CreaturePlateReading, CreaturePlateReport } from "./report.ts";

/**
 * Creature nameplates on the canvas (R-0089 and R-0094, ADR-082's notes of 2026-10-02; the design's NPC and creature
 * nameplates, the same pixel size as a player's plate): every creature drawn carries a plate of three display objects,
 * its attitude mark 2 logical px left of the text as a player's flag mark stands, its level as a number in the muted
 * text colour, and its template's name in its attitude's colour after it, as the design's "8 Marsh Stalker" (`style.ts`).
 * Its health bar is the health bars surface's (`health-bars/`), shown once the creature is damaged or while it is the
 * player's target, and the plate stands the
 * bar's height and a pixel higher while it shows, as a player's does. The plate stands over the head the creature's
 * own sheet reaches (`creature-heads.ts`), drawn by the UI camera, so a plate pixel is a HUD pixel at every world zoom.
 *
 * Creatures follow other players' plates in the viewer's Nameplates choices (`creaturePlateForm`): a name choice draws
 * the whole plate, Flag only the mark alone and Off nothing. A dead creature's plate is hidden while its body lies.
 * Plates come from a pool keyed by handle (`nameplates/pool.ts`), so a field of many creatures makes a plate the first
 * time it holds that many and reuses them after; a plate is hidden, and nothing about it is updated, while its creature
 * is concealed from the viewer and not targeted (ADR-061's amendment for P1.9-T73), outside the vision cap around the local character (ADR-061 decision 6) or off the canvas. Its texts change only when the creature's
 * look, level or form does, and it is laid out only when those, its anchor or the UI scale change, so a frame of
 * steady play sets nothing and allocates nothing but the projection's coordinates.
 *
 * An NPC (ADR-136 decision 1) takes the same plate from its look in the NPC look table: the attitude its plate colour
 * names, and no level for a non-combatant, whose Level on the wire is a constant (`npcFights`).
 */

/**
 * The widest plate in logical pixels before what it draws is known: the mark, the gap, the widest level line, the
 * level gap and a name at its cap in the widest glyphs.
 */
const WIDEST_PLATE = MARK + 2 + 20 + 2 + 195;

/**
 * The level line's tint, the design's muted text colour.
 */
const LEVEL_TINT = Number.parseInt(resolveColour("text-muted").slice(1), 16);

/**
 * One creature's plate and what it shows.
 */
interface CreaturePlate {
  readonly mark: Phaser.GameObjects.Image;
  readonly name: Phaser.GameObjects.BitmapText;
  readonly level: Phaser.GameObjects.BitmapText;
  look: CreatureLook | NpcLook | null;
  attitude: Attitude | null;
  levelValue: number;
  form: CreaturePlateForm;
  nameWidth: number;
  levelWidth: number;
  anchorX: number;
  anchorY: number;
  scale: number;
  lifted: boolean;
  shown: boolean;
}

/**
 * Hides a plate's objects, if shown.
 *
 * @param plate The plate.
 */
function hide(plate: CreaturePlate): void {
  if (plate.shown) {
    plate.mark.setVisible(false);
    plate.name.setVisible(false);
    plate.level.setVisible(false);
    plate.shown = false;
  }
}

/**
 * The creature nameplates surface.
 *
 * @param report The game report's `creaturePlates` entry.
 * @param choices The viewer's Nameplates choices as they stand, read each frame (the nameplates' report).
 * @returns The surface's module, listed after the health bars.
 */
export function creaturePlates(report: CreaturePlateReport, choices: Readonly<NameplateChoices>): WorldUiModule {
  return {
    id: "creature-plates",
    /**
     * Builds the pool and the frame's visitor, and lists the plates for the report.
     *
     * @param context The UI layer, the projection and the characters the scene draws, creatures among them.
     * @returns The surface.
     */
    create(context) {
      const scene = context.scene;
      const plates = new NameplatePool<CreaturePlate>(() => {
        const mark = context.add(new Phaser.GameObjects.Image(scene, 0, 0, UI_ATLAS_KEY, ATTITUDE_STYLES.hostile.mark));
        mark.setOrigin(0, 0).setVisible(false);
        const name = context.add(new Phaser.GameObjects.BitmapText(scene, 0, 0, PIXEL_FONT_KEY, "", pixelFontSize("nameplate")));
        name.setOrigin(0, 0).setVisible(false);
        const level = context.add(new Phaser.GameObjects.BitmapText(scene, 0, 0, PIXEL_FONT_KEY, "", pixelFontSize("nameplate")));
        level.setOrigin(0, 0).setTint(LEVEL_TINT).setVisible(false);
        return { mark, name, level, look: null, attitude: null, levelValue: -1, form: "none", nameWidth: 0, levelWidth: 0, anchorX: Number.NaN, anchorY: Number.NaN, scale: 0, lifted: false, shown: false };
      });
      const local = { x: 0, y: 0 };
      const anchor = { x: 0, y: 0 };
      const layout: PlateLayout = newPlateLayout();
      const camera = context.worldCamera;
      let frame = 0;
      let shown = 0;
      let targetHandle = -1;

      /**
       * Returns a plate its creature has left: hidden and holding nothing of it, so the next creature that takes it
       * sets its own look, level and form.
       *
       * @param plate The plate.
       */
      const release = (plate: CreaturePlate): void => {
        hide(plate);
        plate.look = null;
        plate.attitude = null;
        plate.levelValue = -1;
        plate.form = "none";
        plate.anchorX = Number.NaN;
      };

      /**
       * Sets a plate's mark, texts and tints for a creature's or an NPC's look, level and form, counting each text set.
       *
       * @param plate The plate.
       * @param look The creature's or the NPC's look.
       * @param attitude The attitude its look gives it.
       * @param level Its Level component, or -1 for an NPC that shows none.
       * @param form The form it draws in.
       */
      const restyle = (plate: CreaturePlate, look: CreatureLook | NpcLook, attitude: Attitude, level: number, form: Exclude<CreaturePlateForm, "none">): void => {
        if (plate.attitude !== attitude) {
          plate.mark.setFrame(ATTITUDE_STYLES[attitude].mark);
          plate.name.setTint(attitudeTint(attitude));
          plate.attitude = attitude;
        }
        const name = form === "full" ? look.name : "";
        if (plate.name.text !== name) {
          plate.name.setText(name);
          report.texts += 1;
        }
        const line = form === "full" && level >= 0 ? levelLine(level) : "";
        if (plate.level.text !== line) {
          plate.level.setText(line);
          report.texts += 1;
        }
        plate.nameWidth = name === "" ? 0 : plate.name.width;
        plate.levelWidth = line === "" ? 0 : plate.level.width;
        plate.look = look;
        plate.levelValue = level;
        plate.form = form;
        plate.anchorX = Number.NaN;
      };

      /**
       * Brings one drawn creature's or NPC's plate up to date for the frame; a player character is not this surface's.
       *
       * @param handle The entity's handle.
       * @param x Its feet across, in world pixels.
       * @param y Its feet down, in world pixels.
       * @param state Its state, with its kind, appearance, level and Health percentage.
       * @param head How far its art reaches above its feet, in world pixels.
       * @param hidden Whether its world UI hides (ADR-061's amendment for P1.9-T73): concealed from the viewer and not
       *   targeted; its plate is kept, hidden, so it shows the frame the creature does.
       */
      const visit = (handle: number, x: number, y: number, state: EntityState, head: number, hidden: boolean): void => {
        if (state.kind !== EntityKind.Creature && state.kind !== EntityKind.Npc) {
          return;
        }
        const plate = plates.take(handle, frame);
        const form = creaturePlateForm(choices);
        const npcLook = state.kind === EntityKind.Npc ? context.characters.npcLook(state.appearance) : null;
        const look = npcLook ?? (state.kind === EntityKind.Creature ? context.characters.creatureLook(state.appearance) : null);
        if (hidden || form === "none" || look === null || state.health === 0 || !inVisionCap(x - local.x, y - local.y)) {
          hide(plate);
          return;
        }
        const scale = context.scale.current;
        const lifted = creatureBarShown(state.health, handle === targetHandle, choices);
        context.project(x, y - head, anchor);
        const anchorX = Math.round(anchor.x * scale);
        const anchorY = Math.round(anchor.y * scale) - (lifted ? PLATE_LIFT * scale : 0);
        const level = npcLook !== null && !npcFights(npcLook.plate) ? -1 : state.level;
        const known = plate.look === look && plate.levelValue === level && plate.form === form;
        const reach = ((known ? plateWidth(MARK, creatureTextWidth(form, plate.nameWidth, plate.levelWidth)) : WIDEST_PLATE) * scale) / 2;
        if (!onScreen(anchorX - reach, anchorY - 32 * scale, 2 * reach, 32 * scale, camera.width, camera.height)) {
          hide(plate);
          return;
        }
        if (!known) {
          restyle(plate, look, npcLook !== null ? attitudeOfPlate(npcLook.plate) : attitudeOf((look as CreatureLook).temperament), level, form);
        }
        if (anchorX !== plate.anchorX || anchorY !== plate.anchorY || scale !== plate.scale) {
          plateLayout(anchorX, anchorY, scale, MARK, creatureTextWidth(form, plate.nameWidth, plate.levelWidth), layout);
          plate.mark.setPosition(layout.flagX, layout.flagY);
          plate.level.setPosition(layout.textX, layout.textY);
          plate.name.setPosition(layout.textX + nameOffset(plate.levelWidth), layout.textY);
          plate.anchorX = anchorX;
          plate.anchorY = anchorY;
          plate.scale = scale;
          plate.lifted = lifted;
          report.layouts += 1;
        }
        if (!plate.shown) {
          plate.mark.setVisible(true);
          plate.name.setVisible(true);
          plate.level.setVisible(true);
          plate.shown = true;
        }
        shown += 1;
      };

      /**
       * Reads every plate held, for the report: made on request, never on a frame.
       *
       * @returns One reading a plate.
       */
      report.list = (): readonly CreaturePlateReading[] => {
        const readings: CreaturePlateReading[] = [];
        plates.forEachActive((plate, handle) => {
          readings.push({
            handle,
            name: plate.name.text,
            level: plate.level.text,
            shown: plate.shown,
            mark: plate.mark.frame.name,
            nameTint: plate.name.tintTopLeft,
            levelTint: plate.level.tintTopLeft,
            markX: plate.mark.x,
            markY: plate.mark.y,
            nameX: plate.name.x,
            nameY: plate.name.y,
            levelX: plate.level.x,
            scale: plate.scale,
            width: plateWidth(MARK, creatureTextWidth(plate.form === "none" ? "mark" : plate.form, plate.nameWidth, plate.levelWidth)),
            lifted: plate.lifted,
            rankFrame: plate.look === null || !("rank" in plate.look) ? 0 : rankFrame(plate.look.rank),
            inUiLayer: plate.mark.displayList === context.layer && plate.name.displayList === context.layer && plate.level.displayList === context.layer,
          });
        });
        return readings;
      };

      return {
        /**
         * Visits every character and creature drawn this frame, then returns the plates of creatures that left.
         */
        update() {
          frame += 1;
          shown = 0;
          if (context.characters.local(local)) {
            targetHandle = context.characters.target();
            context.characters.forEach(visit);
          }
          plates.sweep(frame, release);
          report.active = plates.active;
          report.free = plates.free;
          report.created = plates.created;
          report.shown = shown;
        },
        /**
         * Forgets the report's list, which would hold the destroyed plates.
         */
        destroy() {
          report.list = () => [];
        },
      };
    },
  };
}
